// Tiny anonymous funnel tracker. Fire-and-forget — never blocks UI.
// Visitor ID lives in localStorage so repeat visits are de-duplicated by the
// server cooloff window (30 min for landing_view, 1h for onboarding_start).
import { api } from "./api";

const VISITOR_KEY = "hg_visitor_id";
// Per-page-load sentinel so we never double-fire the same event on a
// single mount. React 18 Strict Mode + dynamic imports were causing 2
// events per visit — that's fixed here.
const firedThisLoad = new Set();

function getVisitorId() {
    try {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id) {
            id = (crypto.randomUUID ? crypto.randomUUID() : `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch {
        // Private mode / storage disabled — generate ephemeral per-call ID.
        return `eph_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
}

// Extract Meta / Google ad tracking parameters + generic UTMs from the URL.
// Also stashes them in sessionStorage the first time we see them so downstream
// events (onboarding_start, onboarding_complete) can be attributed even if the
// user has scrolled / clicked links that dropped the query params.
const UTM_STORAGE_KEY = "hg_utm_v1";
function parseAndPersistAdParams() {
    let stashed = {};
    try {
        const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
        if (raw) stashed = JSON.parse(raw);
    } catch { /* ignore */ }

    let fresh = {};
    try {
        const p = new URLSearchParams(window.location.search);
        const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "gclid"];
        for (const k of keys) {
            const v = p.get(k);
            if (v) fresh[k] = v.slice(0, 120);
        }
    } catch { /* ignore */ }

    // Fresh params win; fall back to previously stashed on same session.
    const merged = { ...stashed, ...fresh };
    if (Object.keys(fresh).length > 0) {
        try { sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
    }
    return merged;
}

export function trackEvent(eventType, meta = {}) {
    try {
        // Per-load dedupe: any given event fires at most once per full page load.
        if (firedThisLoad.has(eventType)) return;
        firedThisLoad.add(eventType);

        const visitor_id = getVisitorId();
        const adParams = parseAndPersistAdParams();
        // Fire-and-forget; swallow errors so analytics never break UX.
        api.post("/track/event", {
            event_type: eventType,
            visitor_id,
            meta: {
                referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
                path: typeof window !== "undefined" ? (window.location.pathname || "/") : "",
                ...adParams,
                ...meta,
            },
        }).catch(() => {});
    } catch {
        // No-op — analytics must never crash the app.
    }
}

// Per-step onboarding drop-off tracker. Unlike trackEvent, dedupes per
// (event_type, step-number) tuple so we get exactly ONE view event per step
// per visitor per page-load — without accidentally suppressing step 4 because
// step 3 already fired. This is what powers the /api/admin/funnel/steps
// waterfall so we can see where in the 10-step quiz users actually bail.
const stepFired = new Set();
export function trackOnboardingStep(step, extra = {}) {
    try {
        const key = `onboarding_step:${step}`;
        if (stepFired.has(key)) return;
        stepFired.add(key);
        const visitor_id = getVisitorId();
        const adParams = parseAndPersistAdParams();
        api.post("/track/event", {
            event_type: "onboarding_step",
            visitor_id,
            meta: {
                step,
                referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
                path: typeof window !== "undefined" ? (window.location.pathname || "/") : "",
                ...adParams,
                ...extra,
            },
        }).catch(() => {});
    } catch { /* no-op */ }
}

// Read persisted UTM params without firing an event. Used by Onboarding to
// pick the "fast lane" step set for paid Meta / TikTok traffic where every
// second of quiz time is a drop-off risk.
export function readAdParams() {
    try {
        const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
}
